import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { AlertBadge } from "@/components/symin/AlertBadge";
import { ExplorationSummaryDialog } from "@/components/symin/ExplorationSummaryDialog";
import { Button } from "@/components/ui/button";
import { Sparkle, PaperPlaneRight, Trash, Robot, User, ArrowSquareOut, CircleNotch } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Summarise this project's prospectivity and the strongest evidence.",
  "What are the top drill targets and why do they rank highest?",
  "Recommend the next exploration phase for the Priority-1 targets.",
  "Which structural corridors are most prospective, and what should we verify in the field?",
];

function Bubble({ role, children }) {
  const isAI = role === "assistant";
  return (
    <div className={`flex gap-3 ${isAI ? "" : "flex-row-reverse"}`} data-testid={`chat-msg-${role}`}>
      <div className="h-8 w-8 rounded-md grid place-items-center shrink-0 border border-border" style={{ background: isAI ? "rgba(168,85,247,0.12)" : "var(--accent, #1A2B47)" }}>
        {isAI ? <Robot size={18} style={{ color: "#A855F7" }} /> : <User size={18} className="text-muted-foreground" />}
      </div>
      <div className={`max-w-[78%] rounded-lg px-4 py-3 text-sm leading-relaxed border ${isAI ? "bg-card border-border" : "bg-primary/10 border-primary/30"}`}>
        {isAI && <AlertBadge level="purple">AI Geoscientist</AlertBadge>}
        <div className={`whitespace-pre-wrap ${isAI ? "mt-2 text-foreground" : "text-foreground"}`}>{children}</div>
      </div>
    </div>
  );
}

export default function AiGeoscientist() {
  const { id } = useParams();
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sid, setSid] = useState(null);
  const scroller = useRef(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/projects/${id}/ai/history`); setSid(r.data.session_id); setMessages(r.data.messages || []); }
      catch (e) { /* silent */ }
    })();
  }, [id]);

  useEffect(() => { const el = scroller.current; if (el) el.scrollTop = el.scrollHeight; }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const r = await api.post(`/projects/${id}/ai/chat`, { message: msg, session_id: sid });
      setSid(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => [...m, { role: "assistant", content: "The AI Geoscientist is temporarily unavailable. Please try again." }]);
    } finally { setSending(false); }
  };

  const clear = async () => {
    try { await api.delete(`/projects/${id}/ai/history`, { params: { session_id: sid } }); setMessages([]); toast.success("Conversation cleared"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="ai" />
      <div className="ml-[64px] flex flex-col h-screen">
        <header className="h-[52px] shrink-0 bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-3 px-5">
          <Sparkle size={18} style={{ color: "#A855F7" }} weight="fill" />
          <h1 className="font-display font-semibold text-base sm:text-lg tracking-tight" data-testid="ai-title">AI Geoscientist</h1>
          <div className="ml-auto flex items-center gap-2">
            <ExplorationSummaryDialog pid={id} />
            {messages.length > 0 && (
              <Button size="sm" variant="outline" className="h-8" onClick={clear} data-testid="ai-clear">
                <Trash size={14} className="mr-1.5" /> Clear
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-8" onClick={() => nav(`/projects/${id}/command`)} data-testid="ai-back-command">
              <ArrowSquareOut size={14} className="mr-1.5" /> Command Centre
            </Button>
          </div>
        </header>

        <div ref={scroller} className="flex-1 min-h-0 overflow-y-auto" data-testid="ai-scroll">
          <div className="max-w-3xl mx-auto px-5 py-6 space-y-5">
            {messages.length === 0 && !sending && (
              <div className="text-center py-8" data-testid="ai-empty">
                <div className="h-14 w-14 rounded-xl grid place-items-center mx-auto mb-4 border border-border" style={{ background: "rgba(168,85,247,0.12)" }}>
                  <Robot size={28} style={{ color: "#A855F7" }} />
                </div>
                <h2 className="font-display font-semibold text-lg">Ask the AI Senior Geoscientist</h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Grounded in this project's spectral, structural and target data. Every answer is a conceptual interpretation requiring field verification.</p>
                <div className="grid sm:grid-cols-2 gap-2.5 mt-6 text-left">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} data-testid={`ai-suggestion-${i}`} onClick={() => send(s)}
                      className="px-4 py-3 rounded-lg border border-border bg-card hover:border-[#A855F7]/50 hover:bg-accent/40 transition-colors text-sm text-muted-foreground hover:text-foreground">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => <Bubble key={i} role={m.role}>{m.content}</Bubble>)}
            {sending && (
              <div className="flex gap-3" data-testid="ai-typing">
                <div className="h-8 w-8 rounded-md grid place-items-center shrink-0 border border-border" style={{ background: "rgba(168,85,247,0.12)" }}>
                  <Robot size={18} style={{ color: "#A855F7" }} />
                </div>
                <div className="rounded-lg px-4 py-3 bg-card border border-border flex items-center gap-2 text-sm text-muted-foreground">
                  <CircleNotch size={16} className="animate-spin" style={{ color: "#A855F7" }} /> Interpreting project data…
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Composer */}
        <div className="shrink-0 border-t border-border bg-card">
          <div className="max-w-3xl mx-auto px-5 py-3">
            <div className="flex items-end gap-2 rounded-lg border border-border bg-background px-3 py-2 focus-within:border-[#A855F7]/60 transition-colors">
              <textarea
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1}
                placeholder="Ask about this project's geology, targets or next steps…"
                className="flex-1 resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none max-h-32 py-1"
              />
              <Button size="icon" className="h-9 w-9 shrink-0" disabled={sending || !input.trim()} onClick={() => send()} data-testid="ai-send">
                <PaperPlaneRight size={16} weight="fill" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground text-center mt-2">
              AI-assisted conceptual interpretation · not confirmed mineralisation · always requires qualified geologist review and field verification.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
